import { AfterContentInit, AfterViewInit, Component, Input } from "@angular/core";
import { ProductDetails } from "@brain/data";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { distinctUntilChanged, filter, map, take, takeUntil } from "rxjs/operators";
import { DeloitteAiWindowScrollService } from "../utils/window.scroll";
import { AssetDetailSectionDirective } from "./asset-section";

@Component({
    selector: "brain-asset-detail",
    templateUrl: "./asset-detail.html",
    styleUrls: ["./asset-detail.scss"],
})
export class AssetDetailComponent implements AfterViewInit, AfterContentInit {

  public categories = [];

  public productDetail$: Observable<ProductDetails>;

  @Input()
  public catalog: string;

  @Input()
  public set details(details: ProductDetails) {
      this.assetLoaded$.next(details);
  }

  private assetLoaded$: ReplaySubject<ProductDetails>;

  private destroy$: Subject<boolean> = new Subject();

  /**
   * emits if we change a section, for example if we scroll down
   * the page or click on a menu item
   *
   */
  private sectionChange$: Subject<string>;

  /**
   * all sections we have added
   *
   */
  private sections: Array<AssetDetailSectionDirective> = [];

  /**
   * do not listen to scroll event, this is the case if a menu item is
   * clicked and we scroll down to current position
   *
   */
  private silent = false;

  /**
   * flag content has been initialized, that means all injected sections
   * which are not a part by default of the page and comes through ng-content
   * at the bottom
   *
   */
  private contentInitialized = false;

  /**
   * when injected content has been initialized all other default components
   * will come to the page. InsertMenuItemsBefore is used to know where we add
   * our menu items so they are before the injected sections menu items
   *
   */
  private insertMenuItemsBefore = 0;

  constructor(
    private windowScrollService: DeloitteAiWindowScrollService,
  ) {
      this.assetLoaded$ = new ReplaySubject(1);
      this.productDetail$ = this.assetLoaded$.asObservable();
      this.sectionChange$ = new Subject();
  }

  /**
   * activate category by menu item
   *
   */
  public activateSection(section: string): void {

      this.silent = true;
      this.sectionChange$.next(section);

      const activatedSection = this.sections.find((item) => item.key === section);
      this.windowScrollService.scrollTo({left: 0, top: activatedSection.offsetTop - 100})
          .pipe(take(1))
          .subscribe(() => this.silent = false);
  }

  /**
   * view (included content children) gets completed
   *
   */
  public ngAfterViewInit(): void {
      this.windowScrollService.onChange()
          .pipe(
              takeUntil(this.destroy$),
              filter(() => !this.silent),
              map(() => this.findActiveSection()),
              distinctUntilChanged()
          )
          .subscribe((section) => this.sectionChange$.next(section.key));
  }

  public ngAfterContentInit(): void {
      this.contentInitialized = true;
      this.insertMenuItemsBefore = this.categories.length * -1;
  }

  /**
   * register section, adds a menu entry and will notified if we scrolled
   * down
   *
   */
  public registerSection(section: AssetDetailSectionDirective): void {

      /** ng content comes allways first */
      if (this.categories.indexOf(section.key) !== -1) {
          return;
      }

      if (!this.contentInitialized) {
      /**
       * first comes the ng content so this takes allways place at the end of
       * the page and menu
       */
          this.categories.push(section.key);
          this.sections.push(section);
      } else {
      /**
       * all injected content was passed through now we can add default
       * components. So at this point we put our new menu categories before
       * the injected page sections.
       */
          this.categories.splice(this.insertMenuItemsBefore, 0, section.key);
          this.sections.splice(this.insertMenuItemsBefore, 0, section);
      }
  }

  /**
   *
   *
   */
  public unregisterSection(key: string): void {
      this.categories = this.categories.filter((category) => category !== key);
  }

  /**
   *
   *
   */
  public changeSection(): Observable<string> {
      return this.sectionChange$.asObservable();
  }

  /**
   * find the activated section
   *
   */
  private findActiveSection() {
      let activeSection: AssetDetailSectionDirective;

      for (let i = 0, ln = this.sections.length; i < ln; i++) {
          const section = this.sections[i];
          const bounds  = section.bounds;

          if (bounds.y + bounds.height - 122 < 0) {
              continue;
          }

          activeSection = section;
          break;
      }
      return activeSection ?? this.sections[0];
  }
}
